import dayjs from 'dayjs';
import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';

import { DateTimeFormat, TimeFormat } from '../constants/data';
import BatteryLevel from '../components/GlobalComponents/BatteryLevel/BatteryLevel.jsx';
import {
  AirTemperatureApi,
  DeviceMetrics,
  DeviceMetricsApi,
  DewFallDurationApi,
  DewPointApi,
  GddApi,
  NdviApi,
  NdviValue,
  PhotoApi,
  SensorDailySummaries,
  SensorDailySummariesAdvanced,
  SensorDailySummariesApi,
  SensorDailySummariesImages,
  SummaryPhoto,
  TimelineImage,
} from '../types';

/** ==== Output types ==== */

type SoilSensorDevice = {
  id: string;
  params: { serial_number: string };
};

type DeviceParameter = { title: string; data: ReactNode };

// transformers for api used in store/api modules

/** t = air_temperature {avg, dt_avg, max, min} */
const getAirTemperatureValue = (t: AirTemperatureApi | null | undefined): string | null => {
  return t ? `${t.min < 0 ? '' : '+'}${Math.round(t.min)}...${t.max < 0 ? '' : '+'}${Math.round(t.max)}°C` : null;
};

/** gdd = gdd {dt_value, value} */
const getGDDValue = (gdd: GddApi | null | undefined): number | string => {
  return gdd ? gdd.value : '';
};

/** ndvi = ndvi {avg, dt_avg, max, min, p5, p50, p95} */
const getNdviValue = (ndvi: NdviApi | null | undefined): NdviValue => {
  return ndvi ? { value: ndvi.avg, increasing: ndvi.dt_avg } : { value: 'No data', increasing: null };
};

/** value = dew_point {avg, dt_avg, max, min} */
const getDewPointValue = (value: DewPointApi | null | undefined): string => {
  return value && value.min !== null && value.min !== undefined ? Number(value.min.toFixed(1)) + '°C' : '';
};

/** value = dew_fall_duration {dt_value, value} */
const getDewDurationValue = (value: DewFallDurationApi | null | undefined): number | null => {
  return value ? value.value : null;
};

type GroupedPhotos = { ndvi: SummaryPhoto[]; day: SummaryPhoto[]; night: SummaryPhoto[]; mask: SummaryPhoto[] };

const formatPhotoTakenAt = (takenAt: string): string => {
  const originalTimezone = takenAt.slice(-6);
  return dayjs(takenAt).utcOffset(originalTimezone).format(TimeFormat);
};

/** Groups photos by type (ndvi/day/night/mask) and formats their taken_at timestamp */
const groupAndFormatPhotos = (photos: PhotoApi[] = []): GroupedPhotos => {
  return photos.reduce<GroupedPhotos>(
    (acc, photo) => {
      const photoData: SummaryPhoto = {
        is_night: photo.is_night,
        url: photo.url,
        taken_at: formatPhotoTakenAt(photo.taken_at),
        meta: photo.meta,
      };

      if (photo.type === 'ndvi') acc.ndvi.push(photoData);
      else if (photo.type === 'crop_mask') acc.mask.push(photoData);
      else if (photo.type === 'rgb' && photo.is_night) acc.night.push(photoData);
      else if (photo.type === 'rgb' && !photo.is_night) acc.day.push(photoData);

      return acc;
    },
    { ndvi: [], day: [], night: [], mask: [] },
  );
};

/**
 * Formatting sensors data for showing in dashboard, scoring
 */
export const transformSensorsDailySummary = (data: SensorDailySummariesApi[] = []): SensorDailySummaries[] => {
  return data.map((item: SensorDailySummariesApi) => {
    const {
      id,
      air_temperature,
      air_moisture,
      gdd,
      ndvi,
      dew_point,
      dew_fall_duration,
      bbch_ai,
      disease_ai,
      location,
      photos = [],
      soil_moisture,
      soil_salinity,
      soil_temperature,
    } = item;

    const photosDay = photos.filter((photo) => photo.is_night !== true && photo.type === 'rgb');
    const photosNight = photos.filter((photo) => photo.is_night === true && photo.type === 'rgb');
    const isAiEnable = !!location.sensor.settings?.enabled?.find((setting) => setting.name === 'ai_photo_analysis');

    return {
      id,
      temperature: getAirTemperatureValue(air_temperature),
      gdd: getGDDValue(gdd),
      ndvi: getNdviValue(ndvi),
      dew: getDewPointValue(dew_point),
      dewDuration: getDewDurationValue(dew_fall_duration),
      imageUrl: photos.length ? photos[0].url : null,
      imageUrlDay: photosDay.length ? photosDay[0].url : null,
      imageUrlNight: photosNight.length ? photosNight[0].url : null,
      name: location.sensor.name,
      isFavourite: false,
      location: {
        id: location.id,
        name: location.name,
        gps_point: location.gps_point,
        install_point: location.install_point,
        fieldSeasonId: location.field_season_id,
      },
      field: location.field,
      isAiEnable,
      humidity: air_moisture ? `${Math.round(air_moisture.avg)}%` : '',
      bbchAI: bbch_ai
        ? {
            confidence: bbch_ai?.confidence ? bbch_ai.confidence : null,
            description: bbch_ai?.description ? bbch_ai.description : null,
            dt_value: bbch_ai?.dt_value ? bbch_ai.dt_value : null,
            value: bbch_ai?.value ? bbch_ai.value : null,
          }
        : null,
      diseaseAI: disease_ai
        ? {
            confidence: disease_ai?.confidence ? disease_ai.confidence : null,
            description: disease_ai?.description ? disease_ai.description : null,
            name: disease_ai?.name ? disease_ai.name : null,
          }
        : null,
      soilMoisture: soil_moisture,
      soilSalinity: soil_salinity,
      soilTemperature: soil_temperature,
    };
  });
};

/**
 * Formatting sensors data for showing in charts, fields, map, sensor, timeline
 */
export const transformSensorsDailySummaryAdvanced = (data: SensorDailySummariesApi[] = []): SensorDailySummariesAdvanced[] => {
  return data.map((item) => {
    const {
      id,
      date,
      air_temperature,
      air_moisture,
      gdd,
      ndvi,
      dew_point,
      dew_fall_duration,
      location,
      photos = [],
      bbch_ai,
      disease_ai,
      soil_moisture,
      soil_salinity,
      soil_temperature,
    } = item;

    const { ndvi: photosNDVI, day: photosDay, night: photosNight, mask: photosMask } = groupAndFormatPhotos(photos);

    return {
      id,
      date,
      temperature: air_temperature ? getAirTemperatureValue(air_temperature) : null,
      temperatureAvg: air_temperature ? air_temperature.avg : null,
      temperatureMin: air_temperature ? air_temperature.min : null,
      temperatureMax: air_temperature ? air_temperature.max : null,
      soilMoisture: soil_moisture,
      soilSalinity: soil_salinity,
      soilTemperature: soil_temperature,
      ndvi: {
        value: ndvi ? ndvi.avg : null,
        max: ndvi ? ndvi.max : null,
        min: ndvi ? ndvi.min : null,
        isIncreasing: ndvi ? ndvi.dt_avg > 0 : null,
        p5: ndvi ? ndvi.p5 : null,
        p95: ndvi ? ndvi.p95 : null,
      },
      dew: {
        value: getDewPointValue(dew_point) || null,
        isIncreasing: dew_point ? dew_point.dt_avg > 0 : null,
      },
      dewDuration: getDewDurationValue(dew_fall_duration),
      gdd: {
        value: gdd ? gdd.value : null,
        isIncreasing: gdd ? gdd.dt_value > 0 : null,
      },
      humidity: {
        value: air_moisture ? `${Math.round(air_moisture.avg)}%` : null,
        isIncreasing: air_moisture ? air_moisture.dt_avg > 0 : null,
      },
      photos: {
        ndvi: photosNDVI,
        day: photosDay,
        night: photosNight,
        mask: photosMask,
      },
      bbchAI: bbch_ai,
      diseaseAI: disease_ai,
      location: {
        id: location.id,
        name: location.name,
        field_season_id: location.field_season_id,
        gps_point: location.gps_point,
        install_point: location.install_point,
        install_date: location.install_date,
        removal_date: location.removal_date,
        sensor: location.sensor,
      },
    };
  });
};

/**
 * Formatting sensor preview images for showing in timeline
 */
export const transformSensorImages = (data: SensorDailySummariesApi[] = []): SensorDailySummariesImages => {
  const imagesDay: TimelineImage[] = [];
  const imagesNight: TimelineImage[] = [];
  const imagesNDVI: TimelineImage[] = [];
  data.forEach((obj) => {
    if (obj.photos && obj.photos.length) {
      obj.photos.forEach((item) => {
        const params = {
          temperature: getAirTemperatureValue(obj.air_temperature),
          gdd: getGDDValue(obj.gdd),
          ndvi: getNdviValue(obj.ndvi),
          dewDuration: getDewDurationValue(obj.dew_fall_duration),
          sensor: {
            name: obj.location.sensor.name,
          },
        };
        if (item.type === 'ndvi') {
          imagesNDVI.push({
            ...params,
            date: item.taken_at,
            title: 'ndvi_image',
            url: item.url,
          });
        }
        if (item.type === 'rgb' && !item.is_night) {
          imagesDay.push({
            ...params,
            date: item.taken_at,
            title: 'rgb_day_image',
            url: item.url,
          });
        }
        if (item.type === 'rgb' && item.is_night) {
          imagesNight.push({
            ...params,
            date: item.taken_at,
            title: 'rgb_night_image',
            url: item.url,
          });
        }
      });
    }
  });
  return { imagesDay, imagesNight, imagesNDVI };
};

/**
 * Formatting sensor for showing data in timeline image box
 */
export const transformSensor = (data: SensorDailySummariesApi[] = []) => {
  const sensor = {
    photos: [] as PhotoApi[],
    imgNDVI: {
      takenAt: '',
      url: '',
    },
    imgDay: {
      takenAt: '',
      url: '',
    },
    imgNight: {
      takenAt: '',
      url: '',
    },
    phenostage: 'No stage added',
  };

  if (data.length > 0) {
    const [{ photos = [], bbch }] = data;

    photos.forEach((photo) => {
      const originalTimezone = photo.taken_at.slice(-6);
      const takenAt = dayjs(photo.taken_at).utcOffset(originalTimezone).format(TimeFormat);
      if (photo.type === 'ndvi') {
        sensor.imgNDVI = {
          takenAt,
          url: photo.url ?? '',
        };
      } else {
        if (photo.is_night) {
          sensor.imgNight = {
            takenAt,
            url: photo.url ?? '',
          };
        } else {
          sensor.imgDay = {
            takenAt,
            url: photo.url ?? '',
          };
        }
      }
    });

    sensor.phenostage = bbch ?? 'No stage added';
  }
  return sensor;
};

export const transformSensorDevice = (data: DeviceMetricsApi | null | undefined): DeviceMetrics => {
  let device: DeviceMetrics = {
    id: '',
    firmwareVersion: '',
    sensorVersion: '',
    lastSyncedAt: '',
    hardware: {
      batteryLevel: null,
      storageSize: null,
      storageSizeUsed: null,
    },
    cellular: {
      signalLevel: null,
      networkType: null,
      simCarrier: null,
      localCarrier: null,
      iccid: null,
    },
  };
  if (data) {
    const { id, firmware_version, sensor_version, synced_at, last_cellular_metric, last_hardware_metric } = data;
    device = {
      id: id,
      firmwareVersion: firmware_version,
      sensorVersion: sensor_version,
      lastSyncedAt: synced_at,
      hardware: {
        batteryLevel: last_hardware_metric ? last_hardware_metric.battery_level : null,
        storageSize: last_hardware_metric ? last_hardware_metric.storage_size : null,
        storageSizeUsed: last_hardware_metric ? last_hardware_metric.storage_size_used : null,
      },
      cellular: {
        signalLevel: last_cellular_metric ? last_cellular_metric.signal_level : null,
        networkType: last_cellular_metric ? last_cellular_metric.network_type : null,
        simCarrier: last_cellular_metric ? last_cellular_metric.sim_carrier : null,
        localCarrier: last_cellular_metric ? last_cellular_metric.local_carrier : null,
        iccid: last_cellular_metric ? last_cellular_metric.iccid : null,
      },
    };
  }

  return device;
};

/**
 * Get unique crops for building tabs in dashboard
 */
export const getCrops = (data: { crop_id: string }[]): string[] => [
  ...new Set(data.map(({ crop_id }) => crop_id)),
];

/**
 * Merge dates for selected sensors
 */
export const mergeSensorsDates = (datesSelected: string[], datesCompared: string[]): string[] => [
  ...new Set(
    [...datesSelected, ...datesCompared].sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
  ),
];

/**
 * Return list of device Information
 */
export const listDeviceParameters = ({
  t,
  device,
  gps,
}: {
  t: TFunction;
  device: DeviceMetrics;
  gps: number[];
}): DeviceParameter[] => {
  console.log(device);
  const gpsLink = gps && gps.length > 0 ? `https://www.google.com/maps?q=${gps[1]},${gps[0]}` : undefined;
  const truncateGps = (num: number) => {
    return Math.trunc(num * 10000) / 10000;
  };
  const onCopyGps = async () => {
    try {
      await navigator.clipboard.writeText(`${gps[1]},${gps[0]}`);
    } catch (err) {
      console.log(err);
    }
  };

  return [
    {
      title: t('deviceInfo.name'),
      data: device.id,
    },
    {
      title: t('deviceInfo.battery'),
      data: <BatteryLevel t={t} value={device.hardware.batteryLevel} isIcon={false} />,
    },
    {
      title: t('deviceInfo.sync'),
      data: device.lastSyncedAt ? (
        dayjs(device.lastSyncedAt).format(DateTimeFormat)
      ) : (
        <span className={'text-muted'}>{t('empty.noData')}</span>
      ),
    },
    {
      title: t('deviceInfo.freeSpace'),
      data: `
          ${((device.hardware.storageSizeUsed ?? 0) * 0.000001).toFixed(2)} Gb /
          ${((device.hardware.storageSize ?? 0) * 0.000001).toFixed(2)} Gb
          `,
    },
    {
      title: t('deviceInfo.signal'),
      data: (
        <>
          {device.cellular.signalLevel ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
                <path
                  fill="var(--success)"
                  d="M4 10a.667.667 0 0 0-.667.666v2a.667.667 0 1 0 1.334 0v-2A.667.667 0 0 0 4 9.999Zm2.667-2A.667.667 0 0 0 6 8.665v4a.667.667 0 1 0 1.333 0v-4a.667.667 0 0 0-.666-.667ZM12 2.665a.667.667 0 0 0-.667.667v9.333a.667.667 0 1 0 1.334 0V3.333A.666.666 0 0 0 12 2.666ZM9.333 5.333a.667.667 0 0 0-.666.666v6.667a.667.667 0 1 0 1.333 0V5.999a.667.667 0 0 0-.667-.666Z"
                />
              </svg>
              {device.cellular.signalLevel}
            </>
          ) : (
            <span className={'text-muted'}>{t('empty.noData')}</span>
          )}
        </>
      ),
    },
    {
      title: t('deviceInfo.networkType'),
      data: device.cellular.networkType || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.sim'),
      data: device.cellular.simCarrier || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.localCarrier'),
      data: device.cellular.localCarrier || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.iccid'),
      data: device.cellular.iccid || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.sensorVersion'),
      data: device.sensorVersion || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.firmwareVersion'),
      data: device.firmwareVersion || <span className={'text-muted'}>{t('empty.noData')}</span>,
    },
    {
      title: t('deviceInfo.gpsPoint'),
      data:
        gps && gps.length > 0 ? (
          <>
            <a
              href={gpsLink}
              target="_blank"
              rel="noopener noreferrer"
              className={'text-underline'}
              style={{ color: 'var(--neutral-10)' }}
            >
              {`${truncateGps(gps[1])},${truncateGps(gps[0])} `}
            </a>
            <Button icon={<CroplerIcon name="copy" />} size={'small'} onClick={onCopyGps} />
          </>
        ) : (
          <span className={'text-muted'}>{t('empty.noData')}</span>
        ),
    },
  ];
};

export const listDeviceParametersSoilSensor = ({
  device,
  t,
}: {
  device: SoilSensorDevice;
  t: TFunction;
}): DeviceParameter[] => {
  return [
    {
      title: 'ID',
      data: device.id,
    },
    {
      title: t('deviceInfo.serialNumber'),
      data: device.params.serial_number,
    },
  ];
};
